import {
    ConditionalLogic,
    FormControl,
    FormSchema,
    GFChoice,
    GravityConditionalLogic,
    GravityField,
    GravityForm,
    GravityFormButton,
    iChoice
} from "../types";
import {
    checkboxAdaptor,
    dateAdaptor,
    multiInputAdaptor,
    multiSelectAdaptor,
    radioAdaptor,
    selectAdaptor,
    singleInputAdaptor
} from "./default-value-adaptors";

import createBuilder from "../form-builder";

/**
 * Setup Gravity form submit button
 * @param button
 */
const buttonAdaptor = (button?: GravityFormButton) => ({
    button: {
        ...button,
        conditionalLogic: conditionalLogicAdaptor(button?.conditionalLogic)
    }
});

/**
 * Convert fields to form controls
 * @param fields
 */
const fieldsAdaptor = (fields: GravityField[]) => ({
    controls: fields.map((field: GravityField) => controlAdaptor(field))
});

/**
 * Convert field to form control
 * @param field
 */
const controlAdaptor = (field: GravityField) => ({
    ...field,
    fieldId: field.id,
    id: `field_${field.formId}_${field.id}`,
    name: `input_${field.id}`,
    cssClass: field.cssClass || "",
    conditionalLogic: conditionalLogicAdaptor(field.conditionalLogic),
    ...(field.choices && {
        choices: choicesConditionalLogicAdaptor(field.choices)
    })
});

const choicesConditionalLogicAdaptor = (
    choices?: GFChoice[]
): iChoice[] | undefined => {
    if (choices) {
        return choices.map(choice => {
            return {
                value: choice.value,
                text: choice.text,
                conditionalLogic: choice.conditionalLogic
                    ? conditionalLogicAdaptor(choice.conditionalLogic)
                    : undefined
            };
        });
    } else {
        return undefined;
    }
};

/**
 * Convert conditional logic
 * @param field
 */
const conditionalLogicAdaptor = (
    logic?: GravityConditionalLogic
): ConditionalLogic | undefined =>
    logic
        ? {
              ...logic,
              rules: logic.rules.map(({ fieldId, operator, value }) => ({
                  name: `input_${fieldId}`,
                  operator,
                  value
              }))
          }
        : undefined;

/**
 * Generate allowed extensions
 * @param extensions
 */
const allowedExtensionsAdaptor = (extensions?: string) =>
    extensions &&
    extensions
        .split(",")
        .map((e: string) => `.${e.trim()}`)
        .join(", ");

/**
 * Generate default values object
 * @param fields
 */
const defaultValuesAdaptor = (fields: GravityField[]) => {
    return {
        defaultValues: fields.reduce((values, field) => {
            const args = { values, field };
            switch (field.type) {
                case "select":
                case "conditionalSelect":
                    return selectAdaptor(args);
                case "multiselect":
                    return multiSelectAdaptor(args);
                case "radio":
                case "conditionalRadio":
                    return radioAdaptor(args);
                case "checkbox":
                case "conditionalCheckbox":
                case "tos":
                    return checkboxAdaptor(args);
                case "date":
                case "date_v2":
                    return dateAdaptor(args);
                default:
                    return field.inputs
                        ? multiInputAdaptor(args)
                        : singleInputAdaptor(args);
            }
        }, {})
    };
};

/**
 * Update pages with titles
 * @param titles
 * @param pageIndex
 */
const pageAdaptor = (titles: string[], pageIndex = 0) => (
    control: FormControl
) =>
    control.type === "page"
        ? {
              ...control,
              title: titles[++pageIndex],
              hideNext: control.cssClass.includes("hide-next"),
              hideBack: control.cssClass.includes("hide-back"),
              ...(control.nextButton
                  ? {
                        nextButton: {
                            ...control.nextButton,
                            conditionalLogic: conditionalLogicAdaptor(
                                control.nextButton.conditionalLogic
                            )
                        }
                    }
                  : {})
          }
        : control;

/**
 * Update file fields
 */
const fileAdaptor = () => (control: FormControl) =>
    control.type === "fileupload"
        ? {
              ...control,
              accept: allowedExtensionsAdaptor(control.allowedExtensions),
              maxWidth: 1600,
              maxHeight: 1600
          }
        : control;

const calculationAdaptor = () => (control: FormControl) =>
    control.calculationFormula
        ? {
              ...control,
              calculationFormula: control.calculationFormula.replaceAll(
                  /{([\d_a-zA-Z\-.]+)}/g,
                  "{:$1}"
              )
          }
        : control;

const timeInYearsAndMonthsAdaptor = () => (control: FormControl) =>
    control.type === "years_and_months_field"
        ? {
              ...control,
              valueDelimiter: ":",
              // Year
              0: {
                  isInteger: true,
                  rangeMin: 0,
                  rangeMax: 99
              }
          }
        : control;

/**
 * Convert Gravity Form to Form Schema
 * @param param0
 */
const gravityFormsAdaptor = ({
    id,
    button,
    fields,
    ...form
}: GravityForm): FormSchema & { [key: string]: any } => {
    const titles = (form.pagination || { pages: [] }).pages || [];
    const firstPageProps = {
        cssClass: form.firstPageCssClass,
        form_field_tab_boi_enable_sidebar: form.form_enable_first_page_sidebar,
        form_field_tab_boi_enable_adding_as_stage:
            form.form_enable_first_page_as_stage
    };
    const firstPage =
        (fields[0] || {}).type === "page"
            ? { ...fields[0], ...firstPageProps }
            : { type: "page", id: 0, ...firstPageProps };

    if (form.form_enable_honeypot) {
        const honeypotField ={
            type: "text",
            id: fields.length,
            label: "Email",
            isRequired: false,
            size: "large",
            errorMessage: "",
            visibility: "visible",
            inputs: null,
            formId: id,
            description: "",
            allowsPrepopulate: false,
            inputMask: false,
            maxLength: "",
            descriptionPlacement: "",
            subLabelPlacement: "",
            placeholder: "",
            cssClass: "honeypot",
            inputName: "",
            noDuplicates: false,
            defaultValue: "",
            choices: "",
            conditionalLogic: "",
            multipleFiles: false,
            maxFiles: "",
            calculationFormula: "",
            calculationRounding: "",
            pageNumber: 1
        }
        fields.forEach(field => {
            const fieldId = Number(field.id);
            if (fieldId >= honeypotField.id) honeypotField.id = fieldId + 1;
        });
        fields.unshift(honeypotField);
    }

    const builder = createBuilder(
        Object.assign(
            {
                ...form,
                id: Number(id)
            },
            buttonAdaptor(button),
            fieldsAdaptor([firstPage, ...fields]),
            defaultValuesAdaptor(fields)
        )
    );
    builder.updateControls([
        fileAdaptor(),
        pageAdaptor(titles, -1),
        calculationAdaptor(),
        timeInYearsAndMonthsAdaptor()
    ]);
    return builder.form;
};

export default gravityFormsAdaptor;
